import React from 'react';
import {
  Box,
  ButtonBase,
  CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  AutoAwesomeRounded as AutoAwesomeIcon,
  KeyboardVoiceRounded as VoiceIcon,
  SendRounded as SendIcon,
} from '@mui/icons-material';
import colors from '../../assets/theme/colors';
import {
  ASK_ACCENT,
  ASK_ACCENT_DARK,
  ASK_COLORS,
} from './askRecordUi';

const AskRecordInputForm = ({
  accent,
  canAsk,
  compact = false,
  firstName,
  formSx,
  inputPlaceholder,
  inputSx,
  isFollowUp = false,
  loading,
  onAsk,
  question,
  setQuestion,
}) => {
  const activeAccent = accent || {
    primary: ASK_ACCENT,
    ink: ASK_ACCENT_DARK,
    soft: alpha(ASK_ACCENT, 0.12),
  };
  const accentPrimary = activeAccent.primary || ASK_ACCENT;
  const accentInk = activeAccent.ink || ASK_ACCENT_DARK;
  const accentSoft = activeAccent.soft || alpha(accentPrimary, 0.12);

  return (
  <Box
    component="form"
    onSubmit={(event) => {
      event.preventDefault();
      onAsk();
    }}
    sx={{
      display: 'grid',
      gridTemplateColumns: compact ? 'minmax(0, 1fr) 38px' : '24px minmax(0, 1fr) 42px',
      alignItems: 'center',
      gap: 0.55,
      minHeight: compact ? 48 : 54,
      px: compact ? 0.9 : 1,
      py: 0.55,
      borderRadius: '18px',
      border: 0,
      bgcolor: 'transparent',
      boxShadow: 'none',
      position: 'relative',
      zIndex: 1,
      '&:focus-within': {
        boxShadow: 'none',
      },
      ...formSx,
      ...(isFollowUp ? {
        mt: 0,
        minHeight: { xs: 54, sm: 58 },
        borderRadius: '999px',
        borderColor: alpha(accentPrimary, 0.2),
        bgcolor: '#FFFFFF',
        boxShadow: 'none',
      } : null),
    }}
  >
    {!compact ? (
      <Box
        aria-hidden="true"
        sx={{
          width: 24,
          height: 24,
          display: 'grid',
          placeItems: 'center',
          color: accentPrimary,
        }}
      >
        <AutoAwesomeIcon sx={{ fontSize: 20 }} />
      </Box>
    ) : null}
    <Box
      component="input"
      value={question}
      onChange={(event) => setQuestion(event.target.value)}
      placeholder={isFollowUp ? 'Ask a follow-up...' : inputPlaceholder}
      aria-label={`Ask about ${firstName}'s care`}
      sx={{
        minWidth: 0,
        width: '100%',
        border: 0,
        outline: 0,
        bgcolor: 'transparent',
        color: colors.semantic.ink,
        font: 'inherit',
        fontSize: compact ? '0.86rem' : '0.94rem',
        fontWeight: 650,
        '&::placeholder': {
          color: alpha(ASK_COLORS.inkMuted, 0.68),
        },
        ...inputSx,
      }}
    />
    <ButtonBase
      type="submit"
      disabled={!canAsk}
      aria-label="Ask"
      sx={{
        width: compact ? 34 : 42,
        height: compact ? 34 : 42,
        borderRadius: '999px',
        display: 'grid',
        placeItems: 'center',
        bgcolor: canAsk ? accentPrimary : (isFollowUp ? alpha(colors.semantic.ink, 0.05) : accentSoft),
        color: canAsk ? '#FFFFFF' : (isFollowUp ? alpha(ASK_COLORS.inkMuted, 0.8) : alpha(colors.semantic.ink, 0.36)),
        boxShadow: canAsk ? `0 8px 18px ${alpha(accentPrimary, 0.28)}` : 'none',
        transition: 'background-color 160ms ease, color 160ms ease, box-shadow 160ms ease',
        '&:hover': {
          bgcolor: canAsk ? accentInk : accentSoft,
        },
      }}
    >
      {loading ? <CircularProgress size={16} thickness={5} sx={{ color: 'inherit' }} /> : (
        isFollowUp && !canAsk
          ? <VoiceIcon sx={{ fontSize: compact ? 16 : 20 }} />
          : <SendIcon sx={{ fontSize: compact ? 16 : 20 }} />
      )}
    </ButtonBase>
  </Box>
  );
};

export default AskRecordInputForm;
